import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import type { MealPlan } from "../types";
import { formatDateCs, MEAL_TYPE_LABELS } from "../lib/utils";

export default function MealPlansPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["meal-plans"],
    queryFn: () => api.get<{ mealPlans: MealPlan[] }>("/api/meal-plans"),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/meal-plans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meal-plans"] }),
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Smazání se nezdařilo.";
      alert(msg);
    },
  });

  const list = data?.mealPlans ?? [];
  return (
    <div>
      <PageHeader
        icon="📅"
        title="Jídelníčky"
        description="Plánujte jídelníček na vybrané období."
        actions={
          <Link to="/jidelnicky/novy" className="btn-primary flex items-center gap-1">
            <span aria-hidden>➕</span>
            <span>Nový jídelníček</span>
          </Link>
        }
      />

      {list.length === 0 ? (
        <EmptyState
          icon="📅"
          title="Zatím žádné jídelníčky"
          description="Vytvořte první jídelníček na vybraný rozsah dnů."
          action={
            <Link to="/jidelnicky/novy" className="btn-primary flex items-center gap-1">
              <span aria-hidden>➕</span>
              <span>Vytvořit jídelníček</span>
            </Link>
          }
        />
      ) : (
        <div className="grid gap-3">
          {list.map((p) => (
            <div key={p.id} className="card card-body flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <Link to={`/jidelnicky/${p.id}`} className="font-semibold text-brand-700 hover:underline flex items-center gap-1">
                  <span aria-hidden>📅</span>
                  <span>{p.name}</span>
                </Link>
                <p className="text-xs text-slate-500 mt-1">
                  {formatDateCs(p.dateFrom)} – {formatDateCs(p.dateTo)} ·{" "}
                  {(p.mealTypes ?? []).map((m) => MEAL_TYPE_LABELS[m] ?? m).join(", ")}
                </p>
              </div>
              <div className="flex gap-2">
                <Link to={`/jidelnicky/${p.id}`} className="btn-secondary text-sm flex items-center gap-1">
                  <span aria-hidden>↗</span>
                  <span>Otevřít</span>
                </Link>
                <button
                  type="button"
                  className="btn-ghost text-sm text-red-600 flex items-center gap-1"
                  title="Smazat jídelníček"
                  disabled={del.isPending}
                  onClick={() => {
                    if (confirm(`Smazat jídelníček "${p.name}"?`)) del.mutate(p.id);
                  }}
                >
                  <span aria-hidden>🗑️</span>
                  <span>Smazat</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
